import type { StreamReasoningPart } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

/**
 * Called once per stepId at flush with a merged event whose `message.content`
 * is the join of every chunk pushed for that step. All other metadata is
 * preserved from the LAST event seen.
 */
export type OnMergedReasoningEvent = (mergedEvent: StreamReasoningPart) => void;

export interface ReasoningChunkBuffer {
	/** Buffer a chunk by `stepId`. Returns false (and does NOT buffer) if `stepId` is missing. */
	bufferEvent: (event: StreamReasoningPart) => boolean;
	/** Drain all steps; emit one merged event per step. No-op when empty. */
	flush: () => void;
	/** Number of steps currently buffered (NOT chunks). */
	readonly size: () => number;
}

/**
 * Per-stepId accumulator for REASONING_PART chunks. Replaces N per-chunk
 * dispatches (each with O(N) string concat in appendStepContent) with one
 * merged dispatch per step (O(N) total). Used during page-load reconnect
 * replay to avoid OOM on large streams.
 */
export const createReasoningChunkBuffer = (
	onMergedEvent: OnMergedReasoningEvent,
): ReasoningChunkBuffer => {
	const buffers = new Map<
		string,
		{ chunks: string[]; lastEvent: StreamReasoningPart }
	>();

	const bufferEvent = (event: StreamReasoningPart): boolean => {
		const stepId = event.message.stepId;
		if (!stepId) {
			return false;
		}
		const existing = buffers.get(stepId);
		if (existing) {
			existing.chunks.push(event.message.content);
			existing.lastEvent = event;
		} else {
			buffers.set(stepId, {
				chunks: [event.message.content],
				lastEvent: event,
			});
		}
		return true;
	};

	const flush = () => {
		if (buffers.size === 0) {
			return;
		}
		// Snapshot + clear before invoking the callback so re-entrant flushes are safe.
		const entries = Array.from(buffers.entries());
		buffers.clear();
		for (const [, { chunks, lastEvent }] of entries) {
			onMergedEvent({
				...lastEvent,
				message: {
					...lastEvent.message,
					content: chunks.join(''),
				},
			});
		}
	};

	return {
		bufferEvent,
		flush,
		size: () => buffers.size,
	};
};
